import { useCallback } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { container } from '@/app/container';
import { ContentRepositoryToken } from '../../di/tokens';
import { throwIfEditLocked } from '../display';
import type {
  ContentItemSortField,
  ContentStatus,
  CreateExamQuestionInput,
  ExamPreview,
  MoveExamQuestionsInput,
  SetQuestionAnswerKeyInput,
  ValidateExamImportInput,
} from '../../domain/entities/content.entity';

/** Sem o toggle de arquivadas ligado, a listagem esconde as `ARCHIVED`. */
const ACTIVE_STATUSES: ContentStatus[] = ['DRAFT', 'PUBLISHED'];

export interface UseExamsInput {
  page: number;
  perPage: number;
  /** Ligado = mostra os três status; desligado = só rascunho + publicada. */
  includeArchived: boolean;
  /**
   * Ordenação, resolvida **no banco**. Entra na `queryKey`: sem isso o cache
   * devolveria a página 1 da ordem anterior ao trocar a direção.
   */
  sortBy?: ContentItemSortField;
  sortDirection?: 'asc' | 'desc';
}

export function useExams(input: UseExamsInput) {
  const repository = container.resolve(ContentRepositoryToken);
  return useQuery({
    queryKey: [
      'content',
      'items',
      'exams',
      input.page,
      input.perPage,
      input.includeArchived,
      input.sortBy ?? null,
      input.sortDirection ?? null,
    ],
    queryFn: () =>
      repository.listContentItems({
        page: input.page,
        perPage: input.perPage,
        type: 'EXAM',
        status: input.includeArchived ? undefined : ACTIVE_STATUSES,
        sortBy: input.sortBy,
        sortDirection: input.sortDirection,
      }),
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
  });
}

function examPreviewKey(examId: string | undefined) {
  return ['content', 'exam-preview', examId];
}

export function useExamPreview(examId: string | undefined) {
  const repository = container.resolve(ContentRepositoryToken);
  return useQuery({
    queryKey: examPreviewKey(examId),
    queryFn: () => repository.getExamPreview(examId!),
    enabled: !!examId,
    retry: false,
  });
}

/** Dry-run da importação (passada 2): regras de domínio, sem escrita. */
export function useValidateExamImport() {
  const repository = container.resolve(ContentRepositoryToken);
  return useMutation({
    mutationFn: (input: ValidateExamImportInput) =>
      repository.validateExamImport(input),
  });
}

/**
 * Criação de questão como **função crua**, não `useMutation`: a importação
 * dispara 3 em paralelo pela fila, e o estado de um único `useMutation`
 * compartilhado não descreve nenhuma das três — quem guarda estado por job é a
 * fila. A invalidação de cache fica com quem coordena o lote
 * (`useInvalidateExams`), uma vez no fim, em vez de uma por questão.
 */
export function useCreateExamQuestionRunner() {
  const repository = container.resolve(ContentRepositoryToken);
  return useCallback(
    (examId: string, input: CreateExamQuestionInput) =>
      repository.createExamQuestion(examId, input),
    [repository],
  );
}

/**
 * Relê o preview desta prova e **espera a resposta**.
 *
 * Existe porque `invalidateQueries` é disparo, não garantia: cada escrita do
 * card (corpo, classificação, comentário) invalida o preview e sai uma busca
 * imediatamente, com a foto do servidor **daquele instante**. Quando o mesmo
 * "Salvar" também move a questão de posição, o reorder acontece depois dessas
 * buscas — e a resposta delas, que descreve a ordem ANTERIOR, chega depois do
 * `setQueryData` do reorder e desfaz a mudança na tela. O servidor moveu, a
 * tela não: o card fica no lugar antigo até um F5.
 *
 * Uma releitura no fim, esperada, resolve porque o React Query mantém só a
 * busca mais nova por chave — esta cancela o efeito das anteriores, e o que
 * fica no cache é o estado depois de tudo.
 */
export function useRefetchExamPreview(examId: string | undefined) {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.refetchQueries({ queryKey: examPreviewKey(examId) }),
    [queryClient, examId],
  );
}

export function useInvalidateExams() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['content', 'items'] });
    void queryClient.invalidateQueries({
      queryKey: ['content', 'exam-preview'],
    });
  }, [queryClient]);
}

/**
 * Criação de UMA questão (modal do FAB no preview). Diferente do runner acima
 * por ser `useMutation`: aqui há um botão só esperando uma resposta só, então
 * `isPending`/`error` descrevem exatamente o que a UI precisa mostrar.
 */
export function useCreateExamQuestion(examId: string) {
  const repository = container.resolve(ContentRepositoryToken);
  const invalidate = useInvalidateExams();
  return useMutation({
    mutationFn: (input: CreateExamQuestionInput) =>
      repository.createExamQuestion(examId, input),
    onSuccess: () => invalidate(),
  });
}

/**
 * Exclusão de questão(ões). Serve tanto para o botão de um card quanto para a
 * seleção em massa — quem decide entre hard-delete (rascunho) e tombstone é o
 * backend, e devolve o `mode` que usou.
 */
export function useBulkDeleteQuestions() {
  const repository = container.resolve(ContentRepositoryToken);
  const invalidate = useInvalidateExams();
  return useMutation({
    mutationFn: async (questionIds: string[]) =>
      throwIfEditLocked(await repository.bulkDeleteQuestions(questionIds)),
    onSuccess: () => invalidate(),
  });
}

/**
 * Tira uma questão do **simulado** — desfaz só o apontador (`REFERENCES`); a
 * questão continua na prova dona e em outros simulados. O back renumera o
 * simulado; o refetch do preview traz a ordem nova.
 */
export function useRemoveExamQuestionReference(examId: string) {
  const repository = container.resolve(ContentRepositoryToken);
  const invalidate = useInvalidateExams();
  return useMutation({
    mutationFn: (questionId: string) =>
      repository.removeExamQuestionReference(examId, questionId),
    onSuccess: () => invalidate(),
  });
}

/**
 * Move uma questão para outra posição dentro da prova. O backend renumera a
 * prova inteira em `1..N` e devolve **todas** as arestas já renumeradas — é o
 * que permite atualizar o cache do preview na hora, sem refetch: a resposta já
 * é a verdade sobre a ordem, e o corpo das questões não mudou.
 */
export function useReorderExamQuestion(examId: string) {
  const repository = container.resolve(ContentRepositoryToken);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { questionId: string; newPosition: number }) =>
      repository.reorderExamQuestion(
        examId,
        input.questionId,
        input.newPosition,
      ),
    onSuccess: (links) => {
      queryClient.setQueryData<ExamPreview>(examPreviewKey(examId), (prev) => {
        if (!prev) return prev;
        const positionByQuestionId = new Map(
          links.map((link) => [link.targetContentId, link.position ?? 0]),
        );
        return {
          ...prev,
          questions: prev.questions
            .map((entry) => ({
              ...entry,
              position:
                positionByQuestionId.get(entry.question.id) ?? entry.position,
            }))
            .sort((a, b) => a.position - b.position),
        };
      });
    },
  });
}

/**
 * Gabarito de uma questão: letra correta e/ou "anulada". Serve o lápis da célula
 * do gabarito e o toggle "anulada" do modo edição — os dois são a mesma
 * operação, então é um hook só.
 *
 * Atualiza o cache do preview no lugar, com o body que o back devolveu (que é o
 * body **cru**, com token de imagem). O único campo que a tela lê dele é o
 * gabarito, então mesclar só ele preserva o `stem`/alternativas já renderizados
 * com `<img>` — reescrever o body inteiro com o cru faria as imagens da questão
 * desaparecerem da tela até o próximo refetch.
 */
export function useSetQuestionAnswerKey(examId: string) {
  const repository = container.resolve(ContentRepositoryToken);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetQuestionAnswerKeyInput & { questionId: string }) =>
      repository.setQuestionAnswerKey(input.questionId, {
        correctAlternativeId: input.correctAlternativeId,
        annulled: input.annulled,
      }),
    onSuccess: (updated) => {
      const saved = updated.body as {
        correctAlternativeId?: string;
        annulled?: boolean;
      };
      queryClient.setQueryData<ExamPreview>(examPreviewKey(examId), (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          questions: prev.questions.map((entry) =>
            entry.question.id === updated.id
              ? {
                  ...entry,
                  question: {
                    ...entry.question,
                    version: updated.version,
                    body: {
                      ...(entry.question.body as Record<string, unknown>),
                      correctAlternativeId: saved.correctAlternativeId,
                      annulled: saved.annulled === true,
                    },
                  },
                }
              : entry,
          ),
        };
      });
    },
  });
}

/**
 * Exclusão como **função crua** para a fila (mesma razão do runner de criação):
 * a seleção em massa enfileira uma linha por questão, para o admin ver quais
 * passaram e reenfileirar só as que falharam. O endpoint aceita lista, mas é
 * sequencial e fail-fast no servidor — mandar o lote inteiro numa chamada só
 * transformaria "3 de 12 falharam" num erro único sem dizer onde parou.
 */
export function useBulkDeleteQuestionsRunner() {
  const repository = container.resolve(ContentRepositoryToken);
  return useCallback(
    async (questionIds: string[]) =>
      throwIfEditLocked(await repository.bulkDeleteQuestions(questionIds)),
    [repository],
  );
}

/**
 * Transferência entre provas — uma chamada só para o lote inteiro, ao contrário
 * do delete. A posição no destino é o fim da lista de lá, calculada por questão
 * dentro da transação de cada uma; deixar o servidor percorrer o lote em
 * sequência é o que garante posições distintas sem depender de retry de
 * WriteConflict.
 */
export function useMoveExamQuestionsRunner() {
  const repository = container.resolve(ContentRepositoryToken);
  return useCallback(
    // As que não estavam travadas já foram: a linha da fila falha só para
    // dizer quantas ficaram, e o 409 é permanente, então não oferece reenvio
    // (reenviar o lote moveria de novo as que já passaram).
    async (input: MoveExamQuestionsInput) =>
      throwIfEditLocked(await repository.moveExamQuestions(input)),
    [repository],
  );
}
